import { encodePermissions } from '@erc725/erc725.js'
import { ERC725YDataKeys } from '@lukso/lsp-smart-contracts'
import {
  isAddress,
  padLeft,
  stripHexPrefix,
  toChecksumAddress,
  keccak256,
  toHex,
} from 'web3-utils'
import qs from 'qs'

import { assertObject, assertNonEmptyString } from '@/shared/validators'
import { logInfo } from '@/shared/logger'
import { UpAddress, EoaAddress } from '@/shared/models/Profile'
import { t } from '@/popup/lib/i18n'

export type EthUriFunctions = 'function' | 'address' | 'value'

export type EthUriReturnTypes = {
  chainId: number
  uriType: EthUriFunctions
  functionName?: string
  functionSignature?: string
  functionParameters?: (string | string[])[]
  functionParameterTypes?: string[]
  keys?: string[]
  values?: string[]
  address: string
  value?: string
}

type EthUriParams = {
  address: string
  scheme?: string
  chainId?: number
  functionName?: string
  parameters?: qs.ParsedQs
}

const ADDRESS_REGEX = '(0[xX][\\w]{40})'
const CHAIN_ID_REGEX = '\\@?([\\w]*)*'
const PARAMS_REGEX = '\\/?([\\w]*)*'

const constructFunctionParameters = (text: string) => {
  if (text.startsWith('[') && text.endsWith(']')) {
    return text.slice(1, -1).split(',')
  }
  return text
}

/**
 * Extracts parameters from an ethereum url text.
 *
 * @param uri - ethereum url to be extracted
 * @returns extracted params {@link UrlReturnTypes}
 */
export const extractEthUrl = (
  _uri: string,
  defaultChainId: number
): EthUriReturnTypes => {
  let uri = _uri
  // we add prefix if not already present
  if (uri.split(':')[0] === uri) {
    uri = `ethereum:${uri}`
  }

  const {
    address,
    chainId: parsedChainId,
    functionName,
    parameters,
  } = parse(uri)
  const chainId = parsedChainId ? Number(parsedChainId) : defaultChainId

  // value
  if (parameters?.value && !functionName) {
    assertNonEmptyString(parameters.value, 'Value must be a string')
    return {
      uriType: 'value',
      address,
      value: parameters.value,
      chainId,
    }
  }

  // setData
  if (functionName) {
    assertObject(parameters, 'Invalid setData parameters')
    const { value, ...rest } = parameters
    const functionParameterTypes = Object.keys(rest)
    const functionSignature = keccak256(
      `${functionName}(${functionParameterTypes.join(',')})`
    ).slice(0, 10)
    const functionParameters = []
    for (const key of functionParameterTypes) {
      functionParameters.push(
        constructFunctionParameters(parameters[key] as string)
      )
    }

    return {
      uriType: 'function',
      functionName,
      functionSignature,
      address,
      value: value as string,
      functionParameters,
      chainId,
      functionParameterTypes,
    }
  }

  // address
  return { uriType: 'address', address, chainId }
}

/**
 * Construct an ethereum url eligible for the profile import in
 * the form of ethereum:<address>@<chainId>
 *
 * @param address - hex address
 * @param chainId - chain id
 * @returns ethereum url address string
 */
export const constructAddressEthUrl = (address: UpAddress, chainId: number) => {
  return build({
    scheme: 'ethereum',
    address,
    chainId,
  })
}

/**
 * Construct a large number from exponential eg 1.2e32 to be 1.2000000...0000
 *
 * @param numberInput - number string
 * @returns number string
 */
export const fromNumberExponentToLarge = (
  _numberInput: string,
  localeString?: string
) => {
  let numberInput = _numberInput
  numberInput += '' // To cater to numric entries
  let sign = '' // To remember the number sign
  if (numberInput.charAt(0) === '-') {
    numberInput = numberInput.slice(1)
    sign = '-'
  }
  let splitted = numberInput.split(/[Ee]/g)
  if (splitted.length < 2) return sign + numberInput
  const power = Number.parseInt(splitted[1])
  if (power === 0) return sign + splitted[0]

  const deciSp = (1.1).toLocaleString(localeString).slice(1, 2)
  splitted = splitted[0].split(deciSp)
  let baseRH = splitted[1] || ''
  let baseLH = splitted[0]

  if (power > 0) {
    if (power > baseRH.length) baseRH += '0'.repeat(power - baseRH.length)
    baseRH = baseRH.slice(0, power) + deciSp + baseRH.slice(power)
    if (baseRH.at(-1) == deciSp) baseRH = baseRH.slice(0, -1)
  } else {
    const num = Math.abs(power) - baseLH.length
    if (num > 0) baseLH = '0'.repeat(num) + baseLH
    baseLH = baseLH.slice(0, power) + deciSp + baseLH.slice(power)
    if (baseLH.charAt(0) == deciSp) baseLH = `0${baseLH}`
  }
  return sign + baseLH + baseRH
}

/**
 * Construct an ethereum url to add a new controller
 * the form of ethereum:<address>@<chainId>/setDataBatch?keys=[<keys>]&values=[<values>]
 *
 * @param address address of the profile
 * @param chainId chain id
 * @param currentLength current length of the permissions array
 * @param controllerAddress The address of the controller to add
 * @returns ethereum url setDataBatch string
 */
export const constructURLToAddController = (
  address: UpAddress,
  chainId: number,
  currentLength: number,
  controllerAddress: EoaAddress
) => {
  const keys: string[] = []
  const values: string[] = []

  // key: AddressPermissions:Permissions:<new-controller-key>
  // value: new permissions
  keys.push(
    ERC725YDataKeys.LSP6['AddressPermissions:Permissions'] +
      stripHexPrefix(controllerAddress)
  )
  const permissions = encodePermissions({
    ADDCONTROLLER: true,
    EDITPERMISSIONS: true,
    SUPER_TRANSFERVALUE: true,
    SUPER_CALL: true,
    SUPER_STATICCALL: true,
    DEPLOY: true,
    SUPER_SETDATA: true,
    ENCRYPT: true,
    DECRYPT: true,
    SIGN: true,
    CALL: true,
    STATICCALL: true,
    SETDATA: true,
    TRANSFERVALUE: true,
    EXECUTE_RELAY_CALL: true,
  })
  values.push(permissions)

  // key: AddressPermissions[] (total number of elements key)
  // value: new number of elements
  keys.push(ERC725YDataKeys.LSP6['AddressPermissions[]'].length)
  const lengthPadded = padLeft(toHex(currentLength + 1), 32) // The index is now uint128
  values.push(lengthPadded)

  // key: AddressPermissions[new-index] (index key + new index)
  // value: new controller address
  keys.push(
    ERC725YDataKeys.LSP6['AddressPermissions[]'].index +
      stripHexPrefix(padLeft(toHex(currentLength), 32))
  )
  values.push(controllerAddress)

  const setDataEthUrl = build({
    scheme: 'ethereum',
    address,
    chainId,
    functionName: 'setDataBatch',
    parameters: {
      'bytes32[]': `[${keys}]`,
      'bytes[]': `[${values}]`,
    },
  })

  logInfo('eth url:', setDataEthUrl)

  return setDataEthUrl
}

/**
 * Parse an Ethereum URI according to ERC-831 and ERC-681
 *
 * @param uri - ethereum URI string
 * @return parsed ethereum URI object
 * @example
 * ```js
 * parse('ethereum:0x460fdE74b0748511dAfBc6D42c4d6CE2114A3168@2828?value=1000000000000000000')
 *
 * // returns
 * {
 *   address: '0x460fdE74b0748511dAfBc6D42c4d6CE2114A3168',
 *   chainId: 2828,
 *   functionName: 'transfer',
 *   parameters: { value: '1000000000000000000' },
 * }
 * ```
 */
export function parse(uri: string) {
  if (!uri || typeof uri !== 'string') {
    throw new Error('uri must be a string')
  }

  if (uri.slice(0, 9) !== 'ethereum:') {
    throw new Error('Not an Ethereum ERC-681 URI')
  }

  const fullRegex = `^ethereum:${ADDRESS_REGEX}${CHAIN_ID_REGEX}${PARAMS_REGEX}`

  const exp = new RegExp(fullRegex)
  const data = uri.match(exp)

  if (!data) {
    throw new Error(t('eth_utils.invalid_uri'))
  }

  const [, parametersSegment = ''] = uri.split('?')
  const params = qs.parse(parametersSegment, { depth: 0 })

  if (!isAddress(data[1])) {
    throw new Error('Address used in URI is not valid')
  }

  const object: EthUriParams = {
    scheme: 'ethereum',
    address: toChecksumAddress(data[1]),
  }

  if (data[2]) {
    object.chainId = Number(data[2])
  }

  if (data[3]) {
    object.functionName = data[3]
  }

  if (Object.keys(params).length > 0) {
    object.parameters = params
  }

  return object
}

/**
 * Builds a valid Ethereum URI based on the initial parameters
 *
 * @param object - ethereum URI object
 * @return ethereum URI string
 * @example
 * ```js
 * build(
 *  chainId: 2828,
 *  address: '0x460fdE74b0748511dAfBc6D42c4d6CE2114A3168',
 *  parameters: {
 *    value: '1000000000000000000',
 *  },
 *})
 * // returns
 * 'ethereum:0x460fdE74b0748511dAfBc6D42c4d6CE2114A3168@2828?value=1000000000000000000'
 * ```
 */
export function build(object: EthUriParams) {
  const {
    parameters,
    functionName: _functionName,
    address,
    chainId: _chainId,
  } = object
  const query = parameters
    ? `?${qs.stringify(parameters, { encode: false, arrayFormat: 'comma' })}`
    : ''
  const functionName = _functionName ? `/${_functionName}` : ''
  const chainId = _chainId ? `@${_chainId}` : ''

  return `ethereum:${address}${chainId}${functionName}${query}`
}
